/**
 * App Error Page
 *
 * Full-page error fallback for application-level errors.
 */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';

import type { FallbackProps } from 'react-error-boundary';

export function AppErrorPage({ error, resetErrorBoundary }: FallbackProps) {
  const theme = useTheme();
  const isDev = import.meta.env.DEV;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            textAlign: 'center',
            p: 4,
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.error.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 40, color: 'error.main' }} />
          </Box>

          <Typography variant="h4" gutterBottom fontWeight={700}>
            Something went wrong
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We're sorry, but an unexpected error occurred. Please try refreshing the page or contact
            support if the problem persists.
          </Typography>

          {isDev && error && (
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.05),
                border: 1,
                borderColor: alpha(theme.palette.error.main, 0.2),
                textAlign: 'left',
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  color: 'error.main',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  m: 0,
                }}
              >
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={resetErrorBoundary}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => (window.location.href = '/')}
            >
              Go Home
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}


